import { useEffect, useRef, useCallback, useState, memo } from 'react';
import { tipEmitter } from '../../lib/tipEmitter';
import { getSpineManager, destroySpineManager, type ViewportState } from '../../lib/spine-manager';
import { destroyPixiApp, getPixiApp } from '../../lib/pixi-app';
import { useSceneStore } from '../../stores/sceneStore';
import { useAssetStore } from '../../stores/assetStore';
import { useToolStore } from '../../stores/toolStore';
import { useThemeStore } from '../../stores/themeStore';
import { GridFour, MagnetStraight, Ruler, Trash, CaretLeft, CaretRight, PencilSimple } from '@phosphor-icons/react';
import { getLayerWorldTransform } from '../../lib/transform-utils';
import type { Transform } from '@spine-workspace/shared';
import GridOverlay from './GridOverlay';
import SpineLayerRenderer from './SpineLayerRenderer';
import ImageLayerRenderer from './ImageLayerRenderer';
import TransformGizmo from './TransformGizmo';
import MultiTransformGizmo from './MultiTransformGizmo';
import BoneOverlay from './BoneOverlay';
import IKHandleOverlay from './IKHandleOverlay';
import SafeZoneOverlay from './SafeZoneOverlay';
import AnnotationsOverlay from './AnnotationsOverlay';
import DrawOverlay from './DrawOverlay';
import ReviewDrawOverlay from './ReviewDrawOverlay';
import DrawingToolbar from './DrawingToolbar';
import { useDrawingStore } from '../../stores/drawingStore';
import OnionSkinOverlay from './OnionSkinOverlay';
import GuideOverlay, { useRulerGuideDrag } from './GuideOverlay';
import ToolSwitcher from '../toolbar/ToolSwitcher';
import type { Guide } from './GuideOverlay';
import { useCameraBookmarksStore } from '../../stores/cameraBookmarksStore';
import { Tooltip } from '../ui/tooltip';
import { ContextMenu, ContextMenuTrigger } from '../ui/context-menu';
import CanvasContextMenuContent, { type ContextHit } from './CanvasContextMenu';
import type { Layer } from '@spine-workspace/shared';

const RULER_SIZE = 20; // px

/** Pick a "nice" world-unit step so ticks are spaced ~80-160px apart on screen */
function niceStep(zoom: number): number {
  const targetPx = 120;
  const rawStep = targetPx / zoom;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  if (norm < 1.5) return mag;
  if (norm < 3.5) return 2 * mag;
  if (norm < 7.5) return 5 * mag;
  return 10 * mag;
}

function drawRuler(
  canvas: HTMLCanvasElement,
  orientation: 'h' | 'v',
  vp: ViewportState,
  containerW: number,
  containerH: number,
  dpr: number,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const isLight = document.documentElement.classList.contains('light');
  const rc = isLight
    ? { bg: '#e8e5dc', border: '#c8c5bc', text: '#8c8a82', tick: '#d0cdc5', half: '#dedad2' }
    : { bg: '#1a1a22', border: '#3a3a4a', text: '#6a6a8a', tick: '#3a3a4a', half: '#2a2a38' };

  const { zoom, x: vpX, y: vpY } = vp;
  const step = niceStep(zoom);

  const W = orientation === 'h' ? containerW : RULER_SIZE;
  const H = orientation === 'h' ? RULER_SIZE : containerH;

  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = W + 'px';
  canvas.style.height = H + 'px';
  ctx.scale(dpr, dpr);

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = rc.bg;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = rc.border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (orientation === 'h') { ctx.moveTo(0, H); ctx.lineTo(W, H); }
  else { ctx.moveTo(W, 0); ctx.lineTo(W, H); }
  ctx.stroke();

  ctx.fillStyle = rc.text;
  ctx.font = `9px "JetBrains Mono", monospace`;
  ctx.textBaseline = 'middle';

  if (orientation === 'h') {
    // world origin is at screen x = vpX
    const worldLeft = -vpX / zoom;
    const firstTick = Math.ceil(worldLeft / step) * step;
    for (let w = firstTick; ; w += step) {
      const sx = w * zoom + vpX;
      if (sx > containerW) break;
      if (sx < 0) continue;
      ctx.strokeStyle = rc.tick;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sx, H - 4);
      ctx.lineTo(sx, H);
      ctx.stroke();
      ctx.fillStyle = rc.text;
      ctx.textAlign = 'center';
      ctx.fillText(String(Math.round(w)), sx, H / 2 - 1);
    }
    // half-ticks
    for (let w = firstTick - step / 2; ; w += step) {
      const sx = w * zoom + vpX;
      if (sx > containerW) break;
      if (sx < 0) continue;
      ctx.strokeStyle = rc.half;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sx, H - 3);
      ctx.lineTo(sx, H);
      ctx.stroke();
    }
  } else {
    const worldTop = -vpY / zoom;
    const firstTick = Math.ceil(worldTop / step) * step;
    for (let w = firstTick; ; w += step) {
      const sy = w * zoom + vpY;
      if (sy > containerH) break;
      if (sy < 0) continue;
      ctx.strokeStyle = rc.tick;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(W - 4, sy);
      ctx.lineTo(W, sy);
      ctx.stroke();
      ctx.save();
      ctx.translate(W / 2 - 1, sy);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillStyle = rc.text;
      ctx.fillText(String(Math.round(w)), 0, 0);
      ctx.restore();
    }
  }
}

interface SceneCanvasProps {
  canvasWidth: number;
  canvasHeight: number;
  layers: Layer[];
  readOnly?: boolean;
  onDropAsset?: (assetData: string, x: number, y: number) => void;
}

const SceneCanvas = memo(function SceneCanvas({
  canvasWidth,
  canvasHeight,
  layers,
  readOnly = false,
  onDropAsset,
}: SceneCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const hRulerRef = useRef<HTMLCanvasElement>(null);
  const vRulerRef = useRef<HTMLCanvasElement>(null);
  const initialized = useRef(false);
  const canvasSizeRef = useRef({ w: canvasWidth, h: canvasHeight });
  canvasSizeRef.current = { w: canvasWidth, h: canvasHeight };
  const [vpState, setVpState] = useState<ViewportState>({ zoom: 1, x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const [gizmoBounds, setGizmoBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [multiGizmoBounds, setMultiGizmoBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const dpr = window.devicePixelRatio || 1;

  const { selectedLayerId, selectedLayerIds, selectLayer, backgroundColor, setBackgroundColor } = useSceneStore();
  const selectedLayer = layers.find((l) => l.id === selectedLayerId) ?? null;
  const activeTool = useToolStore((s) => s.activeTool);
  const boneMode = useToolStore((s) => s.boneMode);
  const gridVisible = useToolStore((s) => s.gridVisible);
  const snapEnabled = useToolStore((s) => s.snapEnabled);
  const toggleGrid = useToolStore((s) => s.toggleGrid);
  const toggleSnap = useToolStore((s) => s.toggleSnap);
  const drawingVisible = useDrawingStore((s) => s.visible);
  const toggleDrawingVisible = useDrawingStore((s) => s.toggleVisible);
  const [vpDragging, setVpDragging] = useState(false);
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const [metaHeld, setMetaHeld] = useState(false);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const cmdPanRef = useRef(false); // true while Cmd+drag temporary pan is active

  // Marquee (drag-to-select) state
  const marqueeRef = useRef<{ startX: number; startY: number; active: boolean } | null>(null);
  const [marqueeRect, setMarqueeRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [showGuides, setShowGuides] = useState(true);
  const { startHRulerDrag, startVRulerDrag, onRulerPointerMove, onRulerPointerUp } =
    useRulerGuideDrag(vpState, guides, setGuides);
  const { bookmarks } = useCameraBookmarksStore();
  const savedSlots = Object.entries(bookmarks).filter(([, v]) => v !== null).map(([k]) => k);

  // No size deps — init runs exactly once. GridOverlay handles artboard/fit on size changes.
  const init = useCallback(async () => {
    if (!containerRef.current || initialized.current) return;
    initialized.current = true;

    const manager = getSpineManager();
    await manager.init(containerRef.current);
    manager.drawArtboard(canvasSizeRef.current.w, canvasSizeRef.current.h);
    manager.fitToArtboard(canvasSizeRef.current.w, canvasSizeRef.current.h);

    // Initial viewport state
    setVpState(manager.getViewportState());

    // Register bone-attachment ticker ONCE here — after PixiJS is ready.
    // Reads directly from Zustand store (getState) to bypass React render timing
    // and always get the freshest layer transforms every frame.
    const app = getPixiApp();
    if (app) {
      app.ticker.add(() => getSpineManager().updateBoneAttachments(useSceneStore.getState().layers));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    init();
    return () => {
      destroySpineManager();
      destroyPixiApp();
      initialized.current = false;
    };
  }, [init]);

  // Subscribe to viewport changes
  useEffect(() => {
    const unsub = getSpineManager().onViewportChange((state) => setVpState(state));
    return unsub;
  }, []);

  // Apply background color to PixiJS artboard
  useEffect(() => {
    getSpineManager().setBackground(backgroundColor ?? null);
  }, [backgroundColor]);

  // Push bone-attachment world transforms to PixiJS when the layers array
  // actually changes (new ref). Filters out unrelated store mutations like
  // selectedLayerId, annotations, etc. that previously caused double invocations
  // with the per-frame ticker callback.
  useEffect(() => {
    const unsub = useSceneStore.subscribe((state, prevState) => {
      if (state.layers !== prevState.layers) {
        getSpineManager().updateBoneAttachments(state.layers);
        getSpineManager().markDirty();
      }
    });
    return unsub;
  }, []);

  // Viewport drag events — for cursor feedback
  useEffect(() => {
    const mgr = getSpineManager();
    const unsubStart = mgr.onViewportDragStart(() => setVpDragging(true));
    const unsubEnd   = mgr.onViewportDragEnd(()   => setVpDragging(false));
    return () => { unsubStart(); unsubEnd(); };
  }, []);

  // Pan mode: disable pointer events on all layer objects so viewport drag works over them
  useEffect(() => {
    getSpineManager().setPanMode(activeTool === 'pan');
  }, [activeTool]);

  // Theme: redraw PixiJS elements that use hardcoded colors
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    getSpineManager().setTheme(theme);
  }, [theme]);

  // Track Ctrl (zoom cursor) and Meta/Cmd (temp-pan cursor) key state
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && !e.metaKey) setCtrlHeld(true);
      if (e.key === 'Meta') setMetaHeld(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey) setCtrlHeld(false);
      if (e.key === 'Meta') setMetaHeld(false);
      // Release temporary pan if Cmd or Ctrl is lifted while still holding mouse button
      if ((e.key === 'Meta' || e.key === 'Control') && cmdPanRef.current) {
        cmdPanRef.current = false;
        getSpineManager().setPanMode(useToolStore.getState().activeTool === 'pan');
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, []);

  // Resize observer — watch the PixiJS mount div (inset by ruler size)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      getSpineManager().resize(width, height);
    });
    ro.observe(el);

    // Also watch the outer wrapper so rulers update
    const outer = el.parentElement;
    if (outer) {
      const outerRo = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        setContainerSize({ w: width, h: height });
      });
      outerRo.observe(outer);
      return () => { ro.disconnect(); outerRo.disconnect(); };
    }
    return () => ro.disconnect();
  }, []);

  // Draw rulers whenever viewport, container size, or theme changes
  useEffect(() => {
    const { w, h } = containerSize;
    if (!w || !h) return;
    if (hRulerRef.current) drawRuler(hRulerRef.current, 'h', vpState, w - RULER_SIZE, h - RULER_SIZE, dpr);
    if (vRulerRef.current) drawRuler(vRulerRef.current, 'v', vpState, w - RULER_SIZE, h - RULER_SIZE, dpr);
  }, [vpState, containerSize, dpr, theme]);

  // Click-to-select: subscribe to spine and empty-area clicks (with modifier support)
  useEffect(() => {
    const mgr = getSpineManager();
    const unsubLayer = mgr.onLayerClick((id, mods) => {
      selectLayer(id, { multi: mods.ctrl, range: mods.shift });
      useAssetStore.getState().selectAsset(null);
    });
    const unsubEmpty = mgr.onEmptyClick(() => selectLayer(null));
    return () => { unsubLayer(); unsubEmpty(); };
  }, [selectLayer]);

  // Update gizmo bounds whenever the selection, viewport, or selected transform changes
  useEffect(() => {
    if (!selectedLayerId || !selectedLayer) {
      setGizmoBounds(null);
      return;
    }
    const b = getSpineManager().getLayerScreenBounds(selectedLayerId);
    setGizmoBounds(b ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayerId, vpState, selectedLayer?.transform]);

  // Compute union bounds for multi-selection gizmo
  useEffect(() => {
    if (selectedLayerIds.length < 2) {
      setMultiGizmoBounds(null);
      return;
    }
    const mgr = getSpineManager();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let found = false;
    for (const id of selectedLayerIds) {
      const b = mgr.getLayerScreenBounds(id);
      if (b) {
        minX = Math.min(minX, b.x);
        minY = Math.min(minY, b.y);
        maxX = Math.max(maxX, b.x + b.width);
        maxY = Math.max(maxY, b.y + b.height);
        found = true;
      }
    }
    setMultiGizmoBounds(found ? { x: minX, y: minY, width: maxX - minX, height: maxY - minY } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayerIds, vpState, layers]);

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/spine-asset');
    if (!data || !onDropAsset) return;

    const rect = containerRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    onDropAsset(data, x, y);
  }

  const zoomPct = Math.round(vpState.zoom * 100);

  // FPS counter — sampled every second from the PixiJS ticker
  const [fps, setFps] = useState<number | null>(null);
  useEffect(() => {
    const id = setInterval(() => {
      const app = getPixiApp();
      if (app) setFps(Math.round(app.ticker.FPS));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Performance degradation indicator — fires when 30-sample rolling avg exceeds 33 ms
  const [perfDegraded, setPerfDegraded] = useState(false);
  useEffect(() => {
    return getSpineManager().onPerfChange((status) => setPerfDegraded(status === 'degraded'));
  }, []);

  // Bone mode: hide PixiJS canvas in 'only' mode so only SVG bone overlay shows
  useEffect(() => {
    const canvas = getPixiApp()?.canvas;
    if (!canvas) return;
    canvas.style.opacity = boneMode === 'only' ? '0' : '1';
    return () => { canvas.style.opacity = '1'; };
  }, [boneMode]);

  // Canvas context menu
  const [contextHit, setContextHit] = useState<ContextHit | null>(null);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [hudCollapsed, setHudCollapsed] = useState(false);

  function handleContextMenu(e: React.MouseEvent) {
    // Use PixiJS-tracked right-click target (set by pointerdown on the layer object itself)
    // — more reliable than coordinate-based hit testing.
    const hit = getSpineManager().getLastRightClick();

    if (hit) {
      setContextHit(hit);
      // Auto-select the right-clicked layer if it isn't already in the selection
      if (!useSceneStore.getState().selectedLayerIds.includes(hit.layerId)) {
        selectLayer(hit.layerId);
      }
      return;
    }

    // PixiJS returned null — either empty space was clicked, OR a React overlay
    // (e.g. the transform gizmo) intercepted the pointer event before PixiJS could see it.
    // Detect the latter: if the click target is outside the PixiJS container, fall back
    // to whichever layer(s) are currently selected.
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      const { selectedLayerIds, layers: storeLayers } = useSceneStore.getState();
      if (selectedLayerIds.length >= 1) {
        const layerId = selectedLayerIds[0];
        const layer = storeLayers.find((l) => l.id === layerId);
        if (layer) {
          setContextHit({ layerId, layerType: layer.layerType as 'spine' | 'image' });
          return;
        }
      }
    }

    setContextHit({ layerId: null, layerType: null });
  }

  function handleFitView() {
    const mgr = getSpineManager();
    mgr.fitToArtboard(canvasWidth, canvasHeight);
    setVpState(mgr.getViewportState());
  }

  // ---- Marquee (drag-to-select) ----
  const RULER_SIZE_PX = 20;
  const MARQUEE_THRESHOLD = 4; // px before marquee activates

  function handleMarqueePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (readOnly || e.button !== 0) return;
    if (!outerRef.current) return;

    // Cmd/Ctrl+drag = temporary pan: bypass marquee, let pixi-viewport handle the drag
    if ((e.metaKey || e.ctrlKey) && activeTool !== 'pan') {
      getSpineManager().setPanMode(true);
      cmdPanRef.current = true;
      return; // don't capture pointer — viewport drag plugin handles it
    }

    if (activeTool !== 'select') return;

    // Ignore clicks on UI elements overlaid on canvas (gizmos, buttons, etc.)
    const target = e.target as Element;
    if (target !== outerRef.current && !containerRef.current?.contains(target)) return;
    if (target.closest('[data-testid^="gizmo"]') || target.closest('[data-testid^="snap-line"]')) return;

    const rect = outerRef.current.getBoundingClientRect();
    const wrapperX = e.clientX - rect.left;
    const wrapperY = e.clientY - rect.top;
    const stageX = wrapperX - RULER_SIZE_PX;
    const stageY = wrapperY - RULER_SIZE_PX;

    // If a layer is under the cursor, let PixiJS handle the click
    const hit = getSpineManager().getLayerAtPoint(stageX, stageY);
    if (hit) return;

    // Start marquee on empty canvas area
    e.stopPropagation();
    outerRef.current.setPointerCapture(e.pointerId);
    getSpineManager().pauseViewportDrag();
    marqueeRef.current = { startX: wrapperX, startY: wrapperY, active: false };
    setMarqueeRect(null);
  }

  function handleMarqueePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!marqueeRef.current || !outerRef.current) return;
    const rect = outerRef.current.getBoundingClientRect();
    const curX = e.clientX - rect.left;
    const curY = e.clientY - rect.top;
    const { startX, startY } = marqueeRef.current;
    const dx = curX - startX;
    const dy = curY - startY;
    if (!marqueeRef.current.active && Math.abs(dx) + Math.abs(dy) < MARQUEE_THRESHOLD) return;
    marqueeRef.current.active = true;
    setMarqueeRect({
      x: Math.min(startX, curX),
      y: Math.min(startY, curY),
      w: Math.abs(dx),
      h: Math.abs(dy),
    });
  }

  function handleMarqueePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    // Restore from Cmd+drag temporary pan
    if (cmdPanRef.current) {
      cmdPanRef.current = false;
      getSpineManager().setPanMode(activeTool === 'pan');
      return;
    }
    if (!marqueeRef.current) return;
    const wasActive = marqueeRef.current.active;
    const savedRect = marqueeRect;
    marqueeRef.current = null;
    setMarqueeRect(null);
    getSpineManager().resumeViewportDrag();

    if (!wasActive || !savedRect || savedRect.w < 2 || savedRect.h < 2) {
      // Tiny drag = single click. PixiJS handles layer selection (pointertap) and
      // empty-canvas deselection (onEmptyClick). Don't call selectLayer(null) here —
      // it would wipe the selection before PixiJS multi-select (Ctrl+click) can fire.
      return;
    }

    // Convert marquee from wrapper-space to stage-space (subtract ruler offset)
    const stageX = savedRect.x - RULER_SIZE_PX;
    const stageY = savedRect.y - RULER_SIZE_PX;
    const ids = getSpineManager().getLayersInScreenRect(stageX, stageY, savedRect.w, savedRect.h);

    if (ids.length === 0) {
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) selectLayer(null);
      return;
    }

    if (e.shiftKey) {
      // Add to existing selection
      const existing = useSceneStore.getState().selectedLayerIds;
      const merged = [...new Set([...existing, ...ids])];
      useSceneStore.getState().selectLayers(merged);
    } else if (e.ctrlKey || e.metaKey) {
      // Toggle: remove if already selected, add if not
      const existing = new Set(useSceneStore.getState().selectedLayerIds);
      ids.forEach((id) => { existing.has(id) ? existing.delete(id) : existing.add(id); });
      useSceneStore.getState().selectLayers([...existing]);
    } else {
      useSceneStore.getState().selectLayers(ids);
    }
  }

  // Cursor: priority order — dragging > pan mode > Cmd (temp pan) > zoom (ctrl) > default
  const vpCursor = vpDragging
    ? 'grabbing'
    : (activeTool === 'pan' || metaHeld || ctrlHeld)
    ? 'grab'
    : undefined;

  return (
    <ContextMenu>
    <ContextMenuTrigger asChild>
    <div
      ref={outerRef}
      className="relative w-full h-full bg-bg-canvas overflow-hidden canvas-dot-grid"
      style={vpCursor ? { cursor: vpCursor } : undefined}
      onContextMenu={readOnly ? undefined : handleContextMenu}
      onPointerDown={readOnly ? undefined : handleMarqueePointerDown}
      onPointerMove={(e) => {
        if (!readOnly) handleMarqueePointerMove(e);
        const rect = outerRef.current?.getBoundingClientRect();
        if (rect) setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }}
      onPointerUp={readOnly ? undefined : handleMarqueePointerUp}
      onPointerLeave={() => setMousePos(null)}
    >
      {/* Ruler corner — themed via CSS variable */}
      <div
        className="absolute top-0 left-0 z-10 border-r border-b border-border-subtle"
        style={{ width: RULER_SIZE, height: RULER_SIZE, backgroundColor: 'rgb(var(--color-bg-deepest))' }}
      />

      {/* Horizontal ruler */}
      <div
        className="absolute top-0 left-[20px] right-0 z-10"
        style={{ height: RULER_SIZE, cursor: 's-resize' }}
        onPointerDown={readOnly ? undefined : startHRulerDrag}
        onPointerMove={readOnly ? undefined : onRulerPointerMove}
        onPointerUp={readOnly ? undefined : onRulerPointerUp}
        onMouseEnter={readOnly ? undefined : () => tipEmitter.emit('Drag from the ruler to create a guide · Double-click a guide to remove it')}
      >
        <canvas ref={hRulerRef} style={{ pointerEvents: 'none' }} />
        {/* Mouse X position marker */}
        {mousePos && !readOnly && (() => {
          const mx = mousePos.x - RULER_SIZE;
          const wx = Math.round((mx - vpState.x) / vpState.zoom);
          const rulerW = containerSize.w - RULER_SIZE;
          const labelLeft = mx < rulerW - 40;
          return (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute top-0 bottom-0 w-px bg-accent opacity-70" style={{ left: mx }} />
              <span
                className="absolute top-1 text-[8px] font-mono text-accent bg-bg-elevated/90 px-0.5 rounded-sm leading-none whitespace-nowrap"
                style={labelLeft ? { left: mx + 3 } : { right: rulerW - mx + 3 }}
              >{wx}</span>
            </div>
          );
        })()}
      </div>

      {/* Vertical ruler */}
      <div
        className="absolute top-[20px] left-0 bottom-0 z-10"
        style={{ width: RULER_SIZE, cursor: 'e-resize' }}
        onPointerDown={readOnly ? undefined : startVRulerDrag}
        onPointerMove={readOnly ? undefined : onRulerPointerMove}
        onPointerUp={readOnly ? undefined : onRulerPointerUp}
        onMouseEnter={readOnly ? undefined : () => tipEmitter.emit('Drag from the ruler to create a guide · Double-click a guide to remove it')}
      >
        <canvas ref={vRulerRef} style={{ pointerEvents: 'none' }} />
        {/* Mouse Y position marker */}
        {mousePos && !readOnly && (() => {
          const my = mousePos.y - RULER_SIZE;
          const wy = Math.round((my - vpState.y) / vpState.zoom);
          const rulerH = containerSize.h - RULER_SIZE;
          const labelTop = my < rulerH - 40;
          return (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute left-0 right-0 h-px bg-accent opacity-70" style={{ top: my }} />
              <span
                className="absolute left-1 text-[8px] font-mono text-accent bg-bg-elevated/90 px-0.5 rounded-sm leading-none whitespace-nowrap"
                style={{
                  ...(labelTop ? { top: my + 3 } : { bottom: rulerH - my + 3 }),
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                }}
              >{wy}</span>
            </div>
          );
        })()}
      </div>

      {/* PixiJS canvas mount — inset by ruler size */}
      <div
        ref={containerRef}
        className="absolute"
        style={{ top: RULER_SIZE, left: RULER_SIZE, right: 0, bottom: 0, cursor: vpCursor }}
        onDragOver={readOnly ? undefined : onDragOver}
        onDrop={readOnly ? undefined : onDrop}
      />

      {/* Tool switcher — top-left canvas overlay */}
      {!readOnly && (
        <div className="absolute z-30 flex items-center gap-1 bg-bg-surface/80 backdrop-blur-sm border border-border rounded-lg px-1.5 py-1 shadow-sm"
          style={{ top: RULER_SIZE + 8, left: RULER_SIZE + 8 }}
        >
          {!toolsCollapsed && <ToolSwitcher />}
          {!toolsCollapsed && <div className="w-px h-4 bg-border mx-0.5" />}
          <Tooltip content={toolsCollapsed ? 'Expand tools' : 'Collapse tools'}>
            <button
              onClick={() => setToolsCollapsed((v) => !v)}
              className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
            >
              {toolsCollapsed ? <CaretRight size={11} /> : <CaretLeft size={11} />}
            </button>
          </Tooltip>
        </div>
      )}

      {/* Drawing toolbar — shown when draw tool is active */}
      {!readOnly && activeTool === 'draw' && (
        <div className="absolute z-30" style={{ top: RULER_SIZE + 52, left: RULER_SIZE + 8 }}>
          <DrawingToolbar />
        </div>
      )}

      {/* React-side overlays */}
      <GridOverlay canvasWidth={canvasWidth} canvasHeight={canvasHeight} />
      {layers.map((layer) =>
        layer.layerType === 'image'
          ? <ImageLayerRenderer key={layer.id} layer={layer} />
          : <SpineLayerRenderer key={layer.id} layer={layer} />
      )}

      {/* Multi-selection gizmo — move-only box around all selected layers */}
      {selectedLayerIds.length > 1 && multiGizmoBounds && activeTool !== 'pan' && (
        <MultiTransformGizmo ids={selectedLayerIds} bounds={multiGizmoBounds} vpState={vpState} />
      )}

      {/* Single-selection transform gizmo — hidden in pan mode, multi-select, or locked layers */}
      {selectedLayerIds.length <= 1 && selectedLayer && !selectedLayer.locked && gizmoBounds && activeTool !== 'pan' && (() => {
        const worldTransform = getLayerWorldTransform(selectedLayer.id, layers);
        const parentWorldTransform: Transform | null = selectedLayer.attachment.parentLayerId
          ? getLayerWorldTransform(selectedLayer.attachment.parentLayerId, layers)
          : null;
        return (
          <TransformGizmo
            layer={selectedLayer}
            worldTransform={worldTransform}
            parentWorldTransform={parentWorldTransform}
            bounds={gizmoBounds}
            vpState={vpState}
          />
        );
      })()}

      {/* Bone overlay — shown in 'on' and 'only' modes */}
      {selectedLayer && boneMode !== 'off' && (
        <BoneOverlay layer={selectedLayer} />
      )}

      {/* IK target handles — draggable, shown with bone overlay */}
      {selectedLayer && selectedLayer.layerType !== 'image' && boneMode !== 'off' && (
        <IKHandleOverlay layer={selectedLayer} readOnly={readOnly} />
      )}

      {/* Safe zone overlay */}
      <SafeZoneOverlay canvasWidth={canvasWidth} canvasHeight={canvasHeight} vpState={vpState} />

      {/* Draw-over overlay */}
      <DrawOverlay readOnly={readOnly} />

      {/* Annotations overlay */}
      <AnnotationsOverlay />

      {/* Review draw overlay — per-version freehand annotations */}
      <ReviewDrawOverlay />

      {/* Onion skin overlay */}
      <OnionSkinOverlay />

      {/* Marquee selection rectangle */}
      {marqueeRect && (
        <div
          className="absolute pointer-events-none z-20 border border-accent/80 bg-accent/10"
          style={{ left: marqueeRect.x, top: marqueeRect.y, width: marqueeRect.w, height: marqueeRect.h }}
        />
      )}

      {/* Ruler guides overlay */}
      {guides.length > 0 && showGuides && (
        <GuideOverlay
          guides={guides}
          vpState={vpState}
          containerW={containerSize.w}
          containerH={containerSize.h}
          onGuidesChange={setGuides}
        />
      )}

      {/* Camera bookmarks indicator — bottom-left */}
      {savedSlots.length > 0 && (
        <div className="absolute bottom-3 left-6 z-10 flex items-center gap-0.5 bg-bg-surface/90 backdrop-blur-sm border border-border rounded-lg px-2 py-1 shadow-sm pointer-events-none">
          <span className="text-[9px] font-mono text-text-tertiary mr-1">Bookmarks:</span>
          {savedSlots.sort().map((k) => (
            <span key={k} className="w-4 h-4 flex items-center justify-center rounded bg-accent/20 text-accent text-[9px] font-mono font-bold">{k}</span>
          ))}
        </div>
      )}

      {/* Canvas controls — bottom-right */}
      <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1 bg-bg-surface/90 backdrop-blur-sm border border-border rounded-lg px-1.5 py-1 shadow-sm">
        {/* Collapse toggle — always visible */}
        <Tooltip content={hudCollapsed ? 'Expand canvas controls' : 'Collapse canvas controls'}>
          <button
            onClick={() => setHudCollapsed((v) => !v)}
            className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
          >
            {hudCollapsed ? <CaretLeft size={11} /> : <CaretRight size={11} />}
          </button>
        </Tooltip>

        {!hudCollapsed && <>
        {fps !== null && (
          <span
            data-testid="fps-counter"
            className={`font-mono text-[10px] px-1 tabular-nums ${
              fps >= 55 ? 'text-emerald-400' : fps >= 30 ? 'text-yellow-400' : 'text-red-400'
            }`}
          >{fps} fps</span>
        )}
        {perfDegraded && (
          <span
            data-testid="perf-degraded-badge"
            className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
            title="Render time exceeds 33 ms — consider reducing scene complexity"
          >slow</span>
        )}
        <div className="w-px h-3 bg-border mx-0.5" />

        {/* Grid toggle */}
        <Tooltip content={gridVisible ? 'Hide grid' : 'Show grid'}>
          <button
            onClick={toggleGrid}
            data-testid="canvas-grid-toggle"
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors duration-fast ${
              gridVisible ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          ><GridFour size={13} /></button>
        </Tooltip>

        {/* Snap toggle */}
        <Tooltip content={snapEnabled ? 'Disable snap' : 'Enable snap'}>
          <button
            onClick={toggleSnap}
            data-testid="canvas-snap-toggle"
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors duration-fast ${
              snapEnabled ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          ><MagnetStraight size={13} /></button>
        </Tooltip>

        {/* Guides toggle */}
        <Tooltip content={showGuides ? 'Hide guides (drag from ruler to create)' : 'Show guides'}>
          <button
            onClick={() => setShowGuides((v) => !v)}
            data-testid="canvas-guides-toggle"
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors duration-fast ${
              guides.length > 0 && showGuides ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          ><Ruler size={13} /></button>
        </Tooltip>

        {/* Clear all guides — only shown when guides exist */}
        {guides.length > 0 && (
          <Tooltip content="Clear all guides">
            <button
              onClick={() => setGuides([])}
              data-testid="canvas-guides-clear"
              className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-colors duration-fast"
            ><Trash size={11} /></button>
          </Tooltip>
        )}

        {/* Drawing visibility toggle */}
        <Tooltip content={drawingVisible ? 'Hide drawings' : 'Show drawings'}>
          <button
            onClick={toggleDrawingVisible}
            data-testid="canvas-drawing-toggle"
            className={`w-6 h-6 flex items-center justify-center rounded transition-colors duration-fast ${
              drawingVisible ? 'text-accent bg-accent-muted' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          ><PencilSimple size={13} /></button>
        </Tooltip>

        {/* BG Color */}
        <Tooltip content="Background color">
          <label className="w-6 h-6 flex items-center justify-center rounded cursor-pointer hover:bg-bg-hover transition-colors duration-fast relative" title="Background color">
            <span
              className="w-3.5 h-3.5 rounded-sm border border-white/20"
              style={backgroundColor
                ? { backgroundColor }
                : { background: 'repeating-conic-gradient(#555 0% 25%, #333 0% 50%) 0 0 / 6px 6px' }
              }
            />
            <input
              type="color"
              value={backgroundColor ?? '#000000'}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="sr-only"
              data-testid="canvas-bg-color"
            />
          </label>
        </Tooltip>
        {backgroundColor !== null && (
          <button
            onClick={() => setBackgroundColor(null)}
            className="text-[9px] font-mono text-text-tertiary hover:text-text-primary px-0.5 rounded hover:bg-bg-hover transition-colors"
            title="Clear background color"
          >×</button>
        )}

        <div className="w-px h-3 bg-border mx-0.5" />

        {/* Zoom controls */}
        <Tooltip content="Zoom out">
          <button
            onClick={() => getSpineManager().zoomOut()}
            data-testid="zoom-out"
            title="Zoom out"
            className="w-6 h-6 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded transition-colors duration-fast font-mono text-base leading-none"
          >−</button>
        </Tooltip>
        <Tooltip content="Reset zoom to fit">
          <button
            onClick={() => {
              const mgr = getSpineManager();
              mgr.fitToArtboard(canvasWidth, canvasHeight);
              setVpState(mgr.getViewportState());
            }}
            data-testid="zoom-reset"
            title="Reset zoom"
            className="font-mono text-xs text-text-secondary hover:text-text-primary px-1 min-w-[42px] text-center rounded hover:bg-bg-hover transition-colors duration-fast"
          >{zoomPct}%</button>
        </Tooltip>
        <Tooltip content="Zoom in">
          <button
            onClick={() => getSpineManager().zoomIn()}
            data-testid="zoom-in"
            title="Zoom in"
            className="w-6 h-6 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded transition-colors duration-fast font-mono text-base leading-none"
          >+</button>
        </Tooltip>
        </>}
      </div>
    </div>
    </ContextMenuTrigger>
    {!readOnly && (
      <CanvasContextMenuContent hit={contextHit} layers={layers} onFitView={handleFitView} />
    )}
    </ContextMenu>
  );
});

export default SceneCanvas;
